import classNames from 'classnames';
import * as d3 from 'd3';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import type { CommonPlotProps } from '../../types';
import {
  getDimmedPlotColor,
  getOtherColor,
  getTextWidth,
  isDarkFill,
  truncateTextWithFit,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { PlotTooltip } from '../plot-tooltip';
import styles from './icicle-chart.module.css';
import {
  computeFocusTransform,
  computeIcicleLayout,
  fitIcicleLevelsToLabels,
  ICICLE_DEFAULT_WIDTH_RATIO,
  ICICLE_GUTTER,
  ICICLE_SEPARATOR,
  type IcicleNode,
  type IcicleSiblingOrder,
  type IcicleSizeTransform,
  icicleCellRect,
  type SerializedIcicleNode,
} from './icicle-layout';
import {
  ancestorAtDepth,
  buildColorPlan,
  ICICLE_ANIMATION_DURATION_MS,
  ICICLE_LABEL_FONT_SIZE,
  ICICLE_LABEL_FONT_WEIGHT,
  ICICLE_LABEL_PADDING_X,
  ICICLE_MIN_LABEL_HEIGHT,
  useIcicleTheme,
} from './icicle-theme';

export type { IcicleNode };

type IcicleClickedNode = {
  id: string;
  name: string;
  depth: number;
  color: string;
};

type IcicleHoveredNode = {
  id: string;
  name: string;
  value: number | undefined;
  depth: number;
};

export type IcicleTooltip = { title: string; valueText?: string };

export type IcicleChartProps = {
  data: IcicleNode[];
  focusId?: string | null;
  onNodeClick?: (node: IcicleClickedNode) => void;
  /** Cells it rejects ignore clicks and keep the default cursor; every cell is clickable without it. */
  isNodeClickable?: (node: { id: string }) => boolean;
  tooltipContent?: (node: IcicleHoveredNode) => IcicleTooltip;
  widthRatio?: number;
  sizeTransform?: IcicleSizeTransform;
  siblingOrder?: IcicleSiblingOrder;
  otherColor?: string;
  fillColor?: string;
} & CommonPlotProps;

const LAYOUT_UNIT = 1000;
const LABEL_INSET_X = 8;
const LABEL_BASELINE_Y = 16;
const MIN_LABEL_WIDTH = 40;
const TRAY_FILL = { light: '#eef2f6', dark: '#414247' } as const;

const defaultTooltipContent = (node: IcicleHoveredNode): IcicleTooltip => ({
  title: node.name,
});

const toHoveredNode = (node: SerializedIcicleNode): IcicleHoveredNode => ({
  id: node.id,
  name: node.name,
  value: node.value,
  depth: node.depth,
});

const nodeIdAt = (target: EventTarget | null): string | null =>
  target instanceof Element
    ? (target.closest('[data-node-id]')?.getAttribute('data-node-id') ?? null)
    : null;

const hitTest = (
  nodes: readonly SerializedIcicleNode[],
  x: number,
  y: number
): SerializedIcicleNode | undefined => {
  let found: SerializedIcicleNode | undefined;
  for (const node of nodes) {
    if (node.depth === 0) continue;
    if (x >= node.y0 && x <= node.y1 && y >= node.x0 && y <= node.x1) {
      found = node;
    }
  }
  return found;
};

export const IcicleChart = ({
  data,
  focusId = null,
  onNodeClick,
  isNodeClickable,
  tooltipContent = defaultTooltipContent,
  widthRatio = ICICLE_DEFAULT_WIDTH_RATIO,
  sizeTransform = 'none',
  siblingOrder = 'deepest-first',
  otherColor,
  fillColor,
  loading = false,
  showNoData,
  className,
}: IcicleChartProps) => {
  const { containerRef, width, height } = useResizeObserver();
  const svgRef = useRef<SVGSVGElement>(null);
  const { theme, fontFamily, colorForSlot } = useIcicleTheme();
  const otherFill = otherColor ?? getOtherColor();

  const [internalFocusId, setInternalFocusId] = useState<string | null>(
    focusId
  );
  const [frame, setFrame] = useState<readonly SerializedIcicleNode[]>([]);
  const [animating, setAnimating] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    content: IcicleTooltip;
  } | null>(null);

  const drawnRef = useRef<readonly SerializedIcicleNode[]>([]);
  const rafRef = useRef<number | null>(null);
  const hasLaidOutRef = useRef(false);
  const prevDataRef = useRef<IcicleNode[] | null>(null);
  const layoutSourceRef = useRef<{
    base: SerializedIcicleNode[];
    focusId: string | null;
  } | null>(null);
  const lastPointerRef = useRef<{ clientX: number; clientY: number } | null>(
    null
  );

  useEffect(() => {
    setInternalFocusId(focusId);
  }, [focusId]);

  const baseLayout = useMemo(
    () =>
      computeIcicleLayout(
        data,
        LAYOUT_UNIT,
        LAYOUT_UNIT,
        widthRatio,
        sizeTransform,
        siblingOrder
      ),
    [data, widthRatio, sizeTransform, siblingOrder]
  );
  const labelledLayout = useMemo(
    () =>
      width <= 0 || height <= 0
        ? baseLayout
        : fitIcicleLevelsToLabels(baseLayout, width, (node) => {
            const boxHeight =
              ((node.x1 - node.x0) * height) / LAYOUT_UNIT - ICICLE_SEPARATOR;
            if (boxHeight < ICICLE_MIN_LABEL_HEIGHT) return 0;
            const textWidth = getTextWidth({
              text: node.name.trim(),
              fontSize: ICICLE_LABEL_FONT_SIZE,
              fontFamily,
              fontWeight: ICICLE_LABEL_FONT_WEIGHT,
            });
            return (textWidth ?? 0) + ICICLE_LABEL_PADDING_X + ICICLE_GUTTER;
          }),
    [baseLayout, width, height, fontFamily]
  );
  const layout = useMemo(
    () =>
      width <= 0 || height <= 0
        ? []
        : computeFocusTransform(labelledLayout, internalFocusId, width, height),
    [labelledLayout, internalFocusId, width, height]
  );

  const stopAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (prevDataRef.current !== data) {
      hasLaidOutRef.current = false;
      prevDataRef.current = data;
    }
    stopAnimation();
    if (layout.length === 0) {
      drawnRef.current = [];
      setFrame([]);
      setAnimating(false);
      setHoveredId(null);
      setTooltip(null);
      return;
    }
    const previous = layoutSourceRef.current;
    const sizeOnly =
      previous !== null &&
      previous.base === baseLayout &&
      previous.focusId === internalFocusId;
    layoutSourceRef.current = { base: baseLayout, focusId: internalFocusId };
    const snap = !hasLaidOutRef.current || sizeOnly;
    hasLaidOutRef.current = true;

    if (snap) {
      drawnRef.current = layout;
      setFrame(layout);
      setAnimating(false);
      return;
    }

    const startById = new Map(drawnRef.current.map((node) => [node.id, node]));
    const start = performance.now();
    setAnimating(true);
    const step = () => {
      const linear = Math.min(
        1,
        (performance.now() - start) / ICICLE_ANIMATION_DURATION_MS
      );
      const eased = d3.easeCubicInOut(linear);
      const next = layout.map((node) => {
        const prev = startById.get(node.id) ?? node;
        return {
          ...node,
          x0: prev.x0 + (node.x0 - prev.x0) * eased,
          x1: prev.x1 + (node.x1 - prev.x1) * eased,
          y0: prev.y0 + (node.y0 - prev.y0) * eased,
          y1: prev.y1 + (node.y1 - prev.y1) * eased,
        };
      });
      drawnRef.current = next;
      setFrame(next);
      if (linear >= 1) {
        rafRef.current = null;
        setAnimating(false);
      } else {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
  }, [layout, baseLayout, internalFocusId, data, stopAnimation]);

  useEffect(() => stopAnimation, [stopAnimation]);

  const byId = useMemo(
    () => new Map(frame.map((node) => [node.id, node])),
    [frame]
  );
  const colorPlan = useMemo(() => buildColorPlan(layout), [layout]);

  const fillForNode = useCallback(
    (node: SerializedIcicleNode): string => {
      const { branchDepth, slots } = colorPlan;
      if (node.depth < branchDepth) return fillColor ?? TRAY_FILL[theme];
      if (fillColor !== undefined) return fillColor;
      const branch = ancestorAtDepth(node, byId, branchDepth);
      if (branch.isOther === true) return otherFill;
      return colorForSlot(slots.get(branch.id) ?? 0);
    },
    [colorPlan, byId, theme, otherFill, fillColor, colorForSlot]
  );

  const showTooltip = useCallback(
    (node: SerializedIcicleNode, clientX: number, clientY: number) => {
      setTooltip({
        x: clientX,
        y: clientY,
        content: tooltipContent(toHoveredNode(node)),
      });
    },
    [tooltipContent]
  );

  const nodeAt = useCallback(
    (
      target: EventTarget | null,
      clientX: number,
      clientY: number
    ): SerializedIcicleNode | undefined => {
      const id = nodeIdAt(target);
      const targeted = id === null ? undefined : byId.get(id);
      if (targeted) return targeted;
      const box = svgRef.current?.getBoundingClientRect();
      if (!box) return undefined;
      return hitTest(frame, clientX - box.left, clientY - box.top);
    },
    [byId, frame]
  );

  useEffect(() => {
    if (animating) return;
    const pointer = lastPointerRef.current;
    if (pointer === null) return;
    const target = document.elementFromPoint(pointer.clientX, pointer.clientY);
    const node =
      svgRef.current?.contains(target) === true
        ? nodeAt(target, pointer.clientX, pointer.clientY)
        : undefined;
    setHoveredId(node?.id ?? null);
    if (node) {
      showTooltip(node, pointer.clientX, pointer.clientY);
    } else {
      setTooltip(null);
    }
  }, [animating, nodeAt, showTooltip]);

  const handleMove = (event: ReactMouseEvent<SVGSVGElement>) => {
    lastPointerRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    if (animating) return;
    const node = nodeAt(event.target, event.clientX, event.clientY);
    setHoveredId(node?.id ?? null);
    if (node) {
      showTooltip(node, event.clientX, event.clientY);
    } else {
      setTooltip(null);
    }
  };

  const handleLeave = () => {
    lastPointerRef.current = null;
    setHoveredId(null);
    setTooltip(null);
  };

  const handleClick = (event: ReactMouseEvent<SVGSVGElement>) => {
    const node = nodeAt(event.target, event.clientX, event.clientY);
    if (!node || isNodeClickable?.(node) === false) return;
    setTooltip(null);
    if (onNodeClick) {
      onNodeClick({
        id: node.id,
        name: node.name,
        depth: node.depth,
        color: fillForNode(node),
      });
    } else {
      const parentFocus = node.depth > 1 ? node.parentId : null;
      setInternalFocusId((current) =>
        node.id === current ? parentFocus : node.id
      );
    }
  };

  const hovered = hoveredId === null ? undefined : byId.get(hoveredId);

  const cells: JSX.Element[] = [];
  const bottomByParent = new Map<string | null, number>();
  for (const node of frame) {
    if (node.depth === 0) continue;
    if (node.y1 < 0 || node.y0 > width || node.x1 < 0 || node.x0 > height) {
      continue;
    }
    const isTray = node.depth < colorPlan.branchDepth;
    const branch = isTray
      ? node
      : ancestorAtDepth(node, byId, colorPlan.branchDepth);
    const { x, y, w, h } = icicleCellRect(
      node,
      branch,
      width,
      height,
      node.isOther === true ? bottomByParent.get(node.parentId) : undefined
    );
    bottomByParent.set(node.parentId, y + Math.max(0, h));
    if (w <= 0 || h <= 0) continue;

    const restFill = fillForNode(node);
    const dimmed = hovered !== undefined && node.id !== hovered.id;

    const label = (() => {
      if (animating) return null;
      const fullyVisible = node.y0 >= 0 && node.y1 <= width + 0.5;
      if (!fullyVisible || w <= MIN_LABEL_WIDTH || h < ICICLE_MIN_LABEL_HEIGHT)
        return null;
      const fitted = truncateTextWithFit({
        text: node.name,
        maxWidth: Math.max(0, Math.floor(w - ICICLE_LABEL_PADDING_X)),
        fontSize: ICICLE_LABEL_FONT_SIZE,
        fontFamily,
        fontWeight: ICICLE_LABEL_FONT_WEIGHT,
      });
      return fitted.fitted ? fitted.text : null;
    })();

    cells.push(
      <g
        key={node.id}
        data-node-id={node.id}
        data-testid={`icicle-node-${node.id}`}
        className={classNames(
          styles.cell,
          isNodeClickable?.(node) !== false && styles.clickable,
          dimmed && styles.dimmed
        )}
        style={
          {
            '--replots-dim-color': getDimmedPlotColor(
              restFill,
              theme,
              colorPlan.slots.get(branch.id) ?? colorPlan.slots.size
            ),
          } as CSSProperties
        }
      >
        <rect x={x} y={y} width={w} height={h} fill={restFill} />
        {label !== null && (
          <text
            x={x + LABEL_INSET_X}
            y={Math.min(y + LABEL_BASELINE_Y, y + h - 4)}
            className={classNames(
              styles.label,
              isDarkFill(restFill)
                ? styles.labelOnDarkCell
                : styles.labelOnLightCell
            )}
          >
            {label}
          </text>
        )}
      </g>
    );
  }

  const hasData = data.length > 0;

  return (
    <div
      data-testid={plotTestId(ReplotChartNames.IcicleChart)}
      className={classNames(styles.container, className)}
    >
      <div ref={containerRef} className={styles.sizingBox}>
        <PlotLoadWrapper
          loading={loading}
          noData={showNoData !== false && !hasData}
        >
          <svg
            ref={svgRef}
            data-testid="icicle-svg"
            className={styles.svg}
            width={width}
            height={height}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            onClick={handleClick}
          >
            {cells}
          </svg>
        </PlotLoadWrapper>
        {tooltip && (
          <PlotTooltip
            isVisible
            x={tooltip.x}
            y={tooltip.y}
            placement="cursor"
            space="viewport"
          >
            <div className={styles.tooltipTitle}>{tooltip.content.title}</div>
            {tooltip.content.valueText !== undefined && (
              <div className={styles.tooltipValue}>
                {tooltip.content.valueText}
              </div>
            )}
          </PlotTooltip>
        )}
      </div>
    </div>
  );
};
